// Abschluss der Meldung in der Schadenmappe (intern statt Kopie + Einwilligung
// des öffentlichen Wizards): die Meldung an den Versicherer — Meldedatum,
// Meldeweg, Schadennummer des Versicherers und seine Ansprechpartner. Jede
// Änderung geht als `onAbschluss(patch)` an die Seite, die sie sofort speichert
// (Spalten des Schadens, keine Meldungsfelder). Die Schadennummer meldet erst
// beim Verlassen des Felds (oder Enter), sonst ginge je Taste ein PUT raus.
import * as React from "react";
import { RadioGroup, StepHead, TextField } from "../../schadenmeldung-big/fields";
import { KontaktSuche, type Verweis } from "./KontaktSuche";
import { heuteIso, istMeldeweg, MELDEWEGE } from "./abschlussPatch";

export type Meldeweg = "Telefon" | "E-Mail" | "Portal" | "Post";

export interface AbschlussWerte {
  reportDate: string | null;
  meldeweg: Meldeweg | null;
  externalDamageNbr: string;
  sachbearbeiterVr: Verweis | null;
  reguliererVr: Verweis | null;
  sachverstaendiger: Verweis | null;
}

export interface AbschlussInternProps {
  werte: AbschlussWerte;
  /**
   * Die Seite meldet ihre Antwort nicht zurück — sie speichert nur, was sich
   * gegenüber ihrem Stand tatsächlich ändert (Kontakte nach Id).
   */
  onAbschluss: (patch: Partial<AbschlussWerte>) => void;
  /**
   * „heute“ darf vorgeschlagen werden (die Seite merkt sich je Schaden, ob das
   * schon geschah — ein bewusst geleertes Datum bleibt leer).
   */
  vorschlagErlaubt: boolean;
}

/** Meldeweg-Optionen, typisiert aus `MELDEWEGE`. */
const MELDEWEG_OPTIONEN = MELDEWEGE.map((m) => ({ value: m, label: m }));

/** Frühestes Jahr, das als Meldedatum gespeichert wird (Zwischenstände wie `0002-…`). */
const MIN_JAHR = 1900;

/** Meldedatum aus dem Datumsfeld: `""` → `null`, unplausibles Jahr → `undefined` (nicht senden). */
function datumAusFeld(wert: string): string | null | undefined {
  if (wert === "") return null;
  const jahr = Number(wert.slice(0, 4));
  return /^\d{4}-\d{2}-\d{2}$/.test(wert) && jahr >= MIN_JAHR ? wert : undefined;
}

type VrFeld = "sachbearbeiterVr" | "reguliererVr" | "sachverstaendiger";

const VR_FELDER: { feld: VrFeld; label: string }[] = [
  { feld: "sachbearbeiterVr", label: "Sachbearbeiter VR" },
  { feld: "reguliererVr", label: "Regulierer" },
  { feld: "sachverstaendiger", label: "Sachverständiger" },
];

/**
 * Ein Kontakt beim Versicherer: „Kontakt ändern“ öffnet nur die Suche (der
 * gespeicherte bleibt, „Abbrechen“ führt zu ihm zurück); erst ein anderer
 * Treffer oder „Entfernen“ wird gemeldet.
 */
function VrKontakt({
  label,
  wert,
  onWert,
}: {
  label: string;
  wert: Verweis | null;
  onWert: (v: Verweis | null) => void;
}) {
  const [suchen, setSuchen] = React.useState(false);
  return (
    <div className="smp-feld" role="group" aria-label={label}>
      <div className="smp-feld-titel">
        {label} <span className="smp-optional">optional</span>
      </div>
      <KontaktSuche
        kontakt={suchen ? null : wert}
        vorheriger={suchen ? wert : null}
        suchLabel={`${label} suchen`}
        platzhalter={`${label} suchen (Name, Firma) …`}
        onWahl={(k) => {
          if (k === null) {
            setSuchen(true);
            return;
          }
          setSuchen(false);
          // Ein anderer Kontakt wird gespeichert; derselbe mit anderem Namen
          // nur lokal übernommen (die Seite vergleicht Ids, Label-only geht nie raus).
          if (k.id !== wert?.id) onWert(k);
          else if (k.label !== wert.label) onWert(k);
        }}
        onName={onWert}
        onEntfernen={() => onWert(null)}
      />
    </div>
  );
}

export function AbschlussIntern({ werte, onAbschluss, vorschlagErlaubt }: AbschlussInternProps) {
  // Erstes Öffnen ohne Meldedatum: heute vorschlagen — und damit sofort
  // speichern. Ob es das erste Öffnen ist, weiß die Seite (`vorschlagErlaubt`);
  // die Ref hält zusätzlich unter StrictMode (Effekt doppelt) dicht.
  const vorbelegt = React.useRef(false);
  const onAbschlussRef = React.useRef(onAbschluss);
  React.useEffect(() => {
    onAbschlussRef.current = onAbschluss;
  });
  // Nur beim Öffnen (Stand des ersten Renders): ein später geleertes Datum
  // bleibt leer.
  const vorschlagen = React.useRef(werte.reportDate === null && vorschlagErlaubt);
  React.useEffect(() => {
    if (!vorschlagen.current || vorbelegt.current) return;
    vorbelegt.current = true;
    onAbschlussRef.current({ reportDate: heuteIso() });
  }, []);

  // Entwurf des Meldedatums: Chromium feuert `change` je Segment (Zwischenwerte
  // wie `0002-…`), gemeldet wird deshalb erst beim Verlassen bzw. Enter.
  const [datum, setDatum] = React.useState(werte.reportDate ?? "");
  const [datumVon, setDatumVon] = React.useState(werte.reportDate);
  if (datumVon !== werte.reportDate) {
    setDatumVon(werte.reportDate);
    setDatum(werte.reportDate ?? "");
  }
  const datumMelden = () => {
    const wert = datumAusFeld(datum);
    if (wert !== undefined && wert !== werte.reportDate) onAbschluss({ reportDate: wert });
  };

  // Entwurf der Schadennummer; ein geänderter Wert von außen ersetzt ihn.
  const [nummer, setNummer] = React.useState(werte.externalDamageNbr);
  const [nummerVon, setNummerVon] = React.useState(werte.externalDamageNbr);
  if (nummerVon !== werte.externalDamageNbr) {
    setNummerVon(werte.externalDamageNbr);
    setNummer(werte.externalDamageNbr);
  }
  const nummerMelden = () => {
    const wert = nummer.trim();
    if (wert !== werte.externalDamageNbr) onAbschluss({ externalDamageNbr: wert });
  };

  return (
    <div className="smp-abschluss">
      <StepHead eyebrow="Abschluss" title="Meldung an den Versicherer" />
      <section className="smp-karte">
        <label className="smp-feld">
          <span className="smp-feld-titel">
            Meldedatum <span className="smp-pflicht">Pflicht</span>
          </span>
          <input
            type="date"
            aria-label="Meldedatum"
            className="smp-suche-feld smp-datum"
            value={datum}
            onChange={(e) => setDatum(e.target.value)}
            onBlur={datumMelden}
            onKeyDown={(e) => {
              if (e.key === "Enter") datumMelden();
            }}
          />
        </label>
        <RadioGroup
          label="Meldeweg"
          options={MELDEWEG_OPTIONEN}
          value={werte.meldeweg}
          onChange={(v) => {
            if (istMeldeweg(v)) onAbschluss({ meldeweg: v });
          }}
          columns
        />
        <div
          onBlur={nummerMelden}
          onKeyDown={(e) => {
            if (e.key === "Enter") nummerMelden();
          }}
        >
          <TextField
            label="Schadennummer des Versicherers"
            value={nummer}
            onChange={setNummer}
            placeholder="Schadennummer des Versicherers"
            optional
          />
        </div>
      </section>
      <section className="smp-karte">
        {VR_FELDER.map(({ feld, label }) => (
          <VrKontakt key={feld} label={label} wert={werte[feld]} onWert={(v) => onAbschluss({ [feld]: v })} />
        ))}
      </section>
    </div>
  );
}
